import { useParams } from "react-router-dom";
import Button from "@mui/material/Button";
import Dialog from "components/dialog";
import WordProcessorField from "components/word-processor-field";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { convertHtmlToText } from "utils/common";
import Typography from "@mui/material/Typography";
interface ExpandableNotesDialogProps {
  show: boolean;
  placeholder: string;
  title: string;
  templateTypes: number[];
  mergeTagsPatientId?: number;
  patientNotSelectedWarning?: string;
  initialValue: string;
  onSubmit: (value: string) => void;
  onClose: () => void;
}
export default function ExpandableNotesDialog({
  title,
  show,
  initialValue,
  mergeTagsPatientId,
  patientNotSelectedWarning,
  placeholder,
  templateTypes,
  onSubmit,
  onClose,
}: ExpandableNotesDialogProps) {
  const params = useParams();
  const defaultValues: { notes: string } = useMemo(() => {
    return { notes: String(initialValue ?? "").replaceAll("\n", "<br />") };
  }, [initialValue]);

  const methods = useForm<{ notes: string }>({
    defaultValues,
  });

  const { control, getValues, reset } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues, show]);

  const handleSubmit = () => {
    const formData = getValues();
    onSubmit(convertHtmlToText(formData.notes));
    onClose();
  };

  const patientId =
    mergeTagsPatientId === -1
      ? undefined
      : mergeTagsPatientId ||
        Number(
          (params.patientId || String(params["*"] ?? "").split("/")[0]) ?? ""
        );

  const disableMergeTags = Boolean(!patientId && patientNotSelectedWarning);

  const templates = useMemo(
    () => (disableMergeTags ? [] : templateTypes),
    [disableMergeTags, templateTypes]
  );

  return (
    <Dialog
      title={title}
      minWidth={1024}
      onClose={onClose}
      open={show}
      dialogContentProps={{
        sx: {
          display: "flex",
          alignItems: "flex-end",
          p: 3,
          flexDirection: "column",
        },
      }}
    >
      {disableMergeTags && (
        <Typography variant="body3" mb={3} width="100%">
          {patientNotSelectedWarning}
        </Typography>
      )}
      <WordProcessorField
        name="notes"
        control={control}
        placeholder={placeholder}
        height={640}
        featuresGroup="textOnly"
        autoFocus
        allowTemplates={!disableMergeTags}
        allowMergeTags={!disableMergeTags}
        allowInsertMergeTagsValues={!disableMergeTags}
        templateTypes={templates}
        allowToolbarAutohide={false}
        mergeTagsPatientId={patientId}
      />
      <Button
        size="small"
        variant="contained"
        sx={{ mt: 2 }}
        onClick={handleSubmit}
      >
        OK
      </Button>
    </Dialog>
  );
}
